import { type FC, useRef, useEffect } from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { Typography } from 'shared/ui/Typography'
import { Scrollbar } from 'shared/ui/Scrollbar'
import { DropdownItem } from 'shared/ui/Dropdown/ui/DropdownItem'
import { Button } from 'shared/ui/Button'
import { IS_APP_LIGHT } from 'shared/config'

import styles from './styles.module.scss'
import type { IDropdownGroupProps, IDropdownItem } from '../types'

export const DropdownGroup: FC<IDropdownGroupProps> = observer(
  ({
    title,
    isScroll = true,
    items = [],
    onChange,
    stylesWrap = {},
    isMultiSelect = false,
    handleMultiSelect,
    handleMultiDeselect,
    showGroupHeader = true,
    keyboardNav,
  }) => {
    const refList = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (refList.current && !IS_APP_LIGHT) {
        refList.current.querySelector('[data-selected="true"]')?.scrollIntoView()
      }
    }, [])

    const getItemKeyboardNav = (item: IDropdownItem) => {
      if (!keyboardNav) return undefined

      return {
        isHighlighted: item.id === keyboardNav.highlightedId,
        isExpanded: item.id === keyboardNav.expandedId,
        onCollapse: keyboardNav.onCollapse,
        parentsDropdownId: keyboardNav.parentsDropdownId,
      }
    }

    if (!showGroupHeader && !items.length) return null

    const renderContent = () => {
      return (
        <>
          {title && showGroupHeader && (
            <div className={styles.titleWrapper}>
              {title && (
                <div className={styles.title}>
                  <Typography
                    variant={'body-sm-medium'}
                    color={'var(--content-primary-tertiary)'}
                    ariaLabel={'DropdownGroupTitle'}
                  >
                    {title}
                  </Typography>
                </div>
              )}
              {isMultiSelect && items.length && (
                <Button
                  text='Select all'
                  typeBtn='text'
                  contained='secondary'
                  size='small'
                  intent={'positive'}
                  textClassname={styles.selectAllButton}
                  onClick={() => {
                    handleMultiSelect?.(items)
                  }}
                />
              )}
              {isMultiSelect && !items.length && (
                <Button
                  text='Deselect all'
                  typeBtn='text'
                  contained='secondary'
                  size='small'
                  intent={'positive'}
                  textClassname={styles.selectAllButton}
                  onClick={() => {
                    if (handleMultiDeselect && title) handleMultiDeselect(title)
                  }}
                />
              )}
            </div>
          )}
          <div className={styles.list} ref={refList}>
            {items.map((item) => (
              <div key={item.id} data-selected={item.activeValue} data-dropdown-id={item.id}>
                <DropdownItem
                  item={item}
                  keyboardNav={getItemKeyboardNav(item)}
                  onChange={(item, parentItem) => {
                    onChange?.(item, parentItem)
                  }}
                />
              </div>
            ))}
          </div>
        </>
      )
    }

    if (isScroll) {
      return (
        <div
          className={classNames(styles.wrap, { [styles.multiSelectWrap]: isMultiSelect })}
          style={stylesWrap}
        >
          <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={192}>
            {renderContent()}
          </Scrollbar>
        </div>
      )
    }

    return (
      <div
        className={classNames(styles.wrap, { [styles.multiSelectWrap]: isMultiSelect })}
        style={stylesWrap}
      >
        {renderContent()}
      </div>
    )
  }
)
